import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import ReactGA from 'react-ga4';
import _ from 'lodash';

import SeeMoreItems from '../../components/SeeMoreItems/SeeMoreItems';
import Button from '../../components/Button/Button';
import { PLAYLIST_LIMIT } from '@jwp/ott-common/src/constants';

import usePlaylist from '@jwp/ott-hooks-react/src/usePlaylist';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import { useFavoritesStore } from '@jwp/ott-common/src/stores/FavoritesStore';
import { getModule } from '@jwp/ott-common/src/modules/container';
import ApiService from '@jwp/ott-common/src/services/ApiService';

import { useScrollRestoration } from '../../hooks/useScrollRestoration';

import styles from './SeeMoreItemsPage.module.scss';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

const SeeMoreItemsPage = (): JSX.Element => {
  const apiService = getModule(ApiService);

  // favourites store
  const favorites = useFavoritesStore((state) => state.getPlaylist());

  const INITIAL_PAGE_OFFSET = 1;
  const location = useLocation();
  const breakpoint = useBreakpoint();
  const { t } = useTranslation('user');

  const [page_offset, setPage_offset] = useState(INITIAL_PAGE_OFFSET);
  const [combinedItems, setCombinedItems] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [activePlaylist, setActivePlaylist] = useState<any>(null);
  const seeMorePlaylistId = location?.pathname.split('/')[1];

  const contentStore = useContentStateStore.getState();

  const { user: customer, subscription } = useAccountStore();
  const pageState = useContentStateStore((s) => s.pages[seeMorePlaylistId]);
  const setPageState = useContentStateStore((s) => s.setPageState);

  const { accessModel, favoritesList, config } = useConfigStore(
    (s) => ({
      accessModel: s.accessModel,
      favoritesList: s.config.features?.favoritesList,
      config: s.config,
    }),
    shallow,
  );

  const { isAnalyticsEnable } = useAppConfig();
  const { click } = useContentStateStore.getState();
  const isLoggedIn = !!customer;
  const userId = isLoggedIn ? customer?.id : 'Guest user';
  const deviceType = useGADeviceType();
  const labelRef = useRef<string | undefined>();

  // Effects

  useEffect(() => {
    const playlistFromLocation = location.state?.playlist;
    const titleFromLocation = location.state?.shelfTitle;
    const playlistFromSession = JSON.parse(sessionStorage.getItem('seeMorePlayList') || 'null');
    const titleFromSession = sessionStorage.getItem('seeMoreShelfTitle');

    const finalPlaylist = playlistFromLocation || playlistFromSession;
    const finalTitle = titleFromLocation || titleFromSession;

    if (finalPlaylist) {
      setActivePlaylist(finalPlaylist);
      sessionStorage.setItem('seeMorePlayList', JSON.stringify(finalPlaylist));
    }
    if (finalTitle) {
      sessionStorage.setItem('seeMoreShelfTitle', finalTitle);
    }
  }, [location.state?.playlist]);

  const { isFetching, error, data: playlist1 } = usePlaylist(seeMorePlaylistId);

  useEffect(() => {
    if (!playlist1?.playlist?.length) {
      setHasMore(false);
      return;
    }

    setCombinedItems((prevItems) => {
      const newItems = playlist1.playlist.filter((item) => item?.mediaid && !prevItems.some((prev) => prev.mediaid === item.mediaid) && item.title);
      const updated = [...prevItems, ...newItems];

      contentStore.setPageState(seeMorePlaylistId, {
        ...contentStore.pages[seeMorePlaylistId],
        totalLoaded: updated.length,
        lastFetched: Date.now(),
      });

      return updated;
    });

    setHasMore(!!playlist1.links?.next);
  }, [playlist1]);

  const playlistList = { ...activePlaylist, playlist: combinedItems };
  const handleLoadMoreClick = async () => {
    const contentStore = useContentStateStore.getState();
    const pageState = contentStore.pages[seeMorePlaylistId];

    // Compute next offset and limit
    const nextPageOffset = (pageState?.pageOffset ?? 0) + PLAYLIST_LIMIT;
    const nextPageLimit = PLAYLIST_LIMIT;

    try {
      const fetchedPlaylist = await apiService.getPlaylistById(seeMorePlaylistId, {
        page_limit: PLAYLIST_LIMIT.toString(),
        page_offset: nextPageOffset.toString(),
      });

      if (!fetchedPlaylist?.links?.next) {
        setHasMore(false);
      }

      if (!fetchedPlaylist?.playlist?.length) return;

      setCombinedItems((prevItems) => {
        const newItems = fetchedPlaylist.playlist.filter((item) => item?.mediaid && !prevItems.some((prev) => prev.mediaid === item.mediaid) && item.title);
        const updated = [...prevItems, ...newItems];

        return updated;
      });

      contentStore.setPageState(seeMorePlaylistId, {
        pageOffset: nextPageOffset,
        pageLimit: nextPageLimit,
        totalLoaded: (pageState?.totalLoaded ?? 0) + fetchedPlaylist.playlist.length,
        lastFetched: Date.now(),
      });
    } catch (err) {
      console.error('Error loading more playlist:', err);
    }
  };

  useEffect(() => {
    if (!isAnalyticsEnable) {
      return;
    }
    const pathname = location.pathname;
    const segments = pathname.split('/');
    const contentIdFromPath = segments[2];
    const activeItem = config?.menu?.find((item) => item.contentId === contentIdFromPath);
    if (pathname === '/') {
      labelRef.current = 'Home';
    } else if (click?.label) {
      labelRef.current = click.label;
    } else if (activeItem?.label) {
      labelRef.current = activeItem?.label;
    }
  }, [combinedItems?.length, favorites?.length, location.pathname, config.menu, click]);

  useEffect(() => {
    if (isAnalyticsEnable) {
      sendGAEvent('screen_view', {
        firebase_screen_class: 'More',
        firebase_screen: `see more | ${location.state?.shelfTitle || sessionStorage.getItem('seeMoreShelfTitle')}`,
        device_type: deviceType,
        userid_no: userId,
      });
    }
  }, []);

  useEffect(() => {
    if (!isAnalyticsEnable) {
      return;
    }

    let lastScrollTop = 0;

    const handleScroll = _.throttle(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const direction = scrollTop > lastScrollTop ? 'down' : 'up';
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

      if (scrollTop > 0) {
        sendGAEvent('scroll', {
          scroll_direction: direction,
          userid_no: userId,
          device_type: deviceType,
        });
      }
    }, 1000);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem('seeMorePlayList');
      sessionStorage.removeItem('seeMoreShelfTitle');
    };
  }, []);

  const mergedItems =
    playlistList.playlist?.length > 0 ? playlistList : location.state?.playlist ?? JSON.parse(sessionStorage.getItem('seeMorePlayList') || 'null');
  const hasPlaylistItem = playlistList?.playlist?.length > 0 || mergedItems?.playlist?.length > 0;
  const hasFavPlaylistItem = seeMorePlaylistId == favorites.feedid && favorites.playlist.length > 0;
  useLayoutEffect(() => {
    // restore scroll
    const restoreScroll = () => {
      if (pageState?.scrollPosition !== undefined) {
        window.scrollTo(0, pageState.scrollPosition);
      } else {
        window.scrollTo(0, 0);
      }
    };

    // Wait for the next animation frame to ensure content is rendered
    const id = requestAnimationFrame(() => {
      restoreScroll();
      requestAnimationFrame(restoreScroll);
    });

    // Capture scroll while user scrolls
    const handleScroll = () => {
      setPageState(seeMorePlaylistId, {
        scrollPosition: window.scrollY,
        from: 'seeMorePlaylistId',
      });
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(id);
      setPageState(seeMorePlaylistId, {
        scrollPosition: window.scrollY,
        from: 'seeMorePlaylistId',
      });
    };
  }, [seeMorePlaylistId]);

  return (
    <div className={styles.favouriteContainer}>
      {(hasPlaylistItem || hasFavPlaylistItem) && (
        <SeeMoreItems
          customer={!!customer}
          playlist={seeMorePlaylistId == favorites.feedid ? favorites : mergedItems}
          accessModel={accessModel}
          hasSubscription={!!subscription}
          title={location.state?.shelfTitle || sessionStorage.getItem('seeMoreShelfTitle')}
        />
      )}
      <div className={styles.loadMore}>
        {hasMore && (
          <Button
            label={t('common:load_more')}
            aria-label={t('loadMore')}
            busy={isFetching}
            onClick={handleLoadMoreClick}
            color={'default'}
            fullWidth={breakpoint < Breakpoint.md}
            size="large"
            type="submit"
          />
        )}
      </div>
    </div>
  );
};

export default SeeMoreItemsPage;
